import { useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/Collapsible';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { cn } from '@/lib/utils';

import { useOpenInputsForCkJobId } from '../ck-debug.hooks';
import { ckDebugStore } from '../ck-debug.store';
import { getSnapshotOutcomeDisplay } from '../ck-debug.utils';

import { CkDebugWindowList } from './ck-debug-window-list';

import type { CkSnapshot } from '../ck-debug.types';

function KeyValue({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <XStack className="justify-between items-baseline py-0.5">
      <span className="text-xs text-foreground-muted shrink-0">{label}</span>
      <span
        className={cn(
          'text-xs text-foreground text-right truncate ml-3',
          mono && 'font-mono tabular-nums'
        )}
      >
        {value}
      </span>
    </XStack>
  );
}

function OutcomeBadgeDetail({ outcome }: { outcome: string }) {
  const display = getSnapshotOutcomeDisplay(outcome);

  return (
    <span
      className={cn(
        'px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase',
        display.bg,
        display.text
      )}
    >
      {outcome}
    </span>
  );
}

function Section({
  title,
  defaultOpen = true,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Collapsible defaultOpen={defaultOpen}>
      <CollapsibleTrigger className="flex items-center gap-1.5 w-full py-1.5 group">
        <Icons.chevronRight className="h-3 w-3 text-foreground-muted transition-transform group-data-[state=open]:rotate-90" />
        <span className="text-xs font-semibold text-foreground uppercase tracking-wide">
          {title}
        </span>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <YStack className="pl-4 pb-2 gap-0.5">{children}</YStack>
      </CollapsibleContent>
    </Collapsible>
  );
}

function formatTimestamp(epochMs: number): string {
  return new Date(epochMs).toLocaleTimeString('en-US', {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    fractionalSecondDigits: 3,
  });
}

export function CkDebugDetailPanel({ snapshot }: { snapshot: CkSnapshot }) {
  const [rawOpen, setRawOpen] = useState(false);
  const openInputsForCkJobId = useOpenInputsForCkJobId();
  const m = snapshot.metrics;

  return (
    <YStack className="p-3 gap-1">
      {/* Header */}
      <XStack className="justify-between items-center pb-2 border-b border-border">
        <XStack className="items-center gap-2 min-w-0">
          <span className="text-sm font-semibold text-foreground truncate">
            {snapshot.appName}
          </span>
          <OutcomeBadgeDetail outcome={snapshot.outcome} />
        </XStack>
        <XStack className="items-center gap-1">
          <Button
            variant="ghost"
            size="xs"
            onClick={() => openInputsForCkJobId(snapshot.jobId)}
          >
            Inputs
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => ckDebugStore.selectSnapshot(null)}
          >
            <Icons.cancel className="h-3.5 w-3.5" />
          </Button>
        </XStack>
      </XStack>

      {/* Overview */}
      <Section title="Overview">
        <KeyValue
          label="Timestamp"
          value={formatTimestamp(snapshot.completedAt)}
          mono
        />
        <KeyValue label="Trigger" value={snapshot.trigger} />
        <KeyValue label="Duration" value={`${m.totalDurationMs}ms`} mono />
        <KeyValue label="Outcome" value={snapshot.outcome} />
        <KeyValue label="Job ID" value={snapshot.jobId} mono />
        <KeyValue label="Bundle ID" value={snapshot.bundleId} mono />
        <KeyValue label="PID" value={String(snapshot.pid)} mono />
        {snapshot.jobFailure && (
          <YStack className="mt-1 p-2 rounded bg-danger-3 text-danger-11 text-xs">
            {snapshot.jobFailure}
          </YStack>
        )}
      </Section>

      {/* Metrics */}
      <Section title="Metrics">
        <KeyValue
          label="Total Duration"
          value={`${m.totalDurationMs}ms`}
          mono
        />
        {m.parsingDurationMs !== null && (
          <KeyValue
            label="Parsing Duration"
            value={`${m.parsingDurationMs}ms`}
            mono
          />
        )}
        <KeyValue
          label="Windows Processed"
          value={String(m.windowsProcessed)}
          mono
        />
        <KeyValue label="Windows Failed" value={String(m.windowsFailed)} mono />
        <KeyValue
          label="Windows Skipped"
          value={String(m.windowsSkipped)}
          mono
        />
        <KeyValue
          label="Not Attempted (Deadline)"
          value={String(m.windowsNotAttemptedDueToDeadline)}
          mono
        />
        <KeyValue
          label="Traversed Dedup"
          value={String(m.traversedItemsDeduplicated)}
          mono
        />
        <KeyValue
          label="Parsed Dedup"
          value={String(m.parsedItemsDeduplicated)}
          mono
        />
        <KeyValue
          label="Screenshot Dedup"
          value={String(m.screenshotsDeduplicated)}
          mono
        />
      </Section>

      {/* Windows */}
      {snapshot.windows.length > 0 && (
        <Section title={`Windows (${snapshot.windows.length})`}>
          <CkDebugWindowList windows={snapshot.windows} />
        </Section>
      )}

      {/* Included Domains */}
      {snapshot.includedDomains.length > 0 && (
        <Section title={`Domains (${snapshot.includedDomains.length})`}>
          {snapshot.includedDomains.map((domain) => (
            <span key={domain} className="text-xs font-mono text-foreground">
              {domain}
            </span>
          ))}
        </Section>
      )}

      {/* Raw JSON */}
      <Collapsible open={rawOpen} onOpenChange={setRawOpen}>
        <CollapsibleTrigger className="flex items-center gap-1.5 w-full py-1.5 group">
          <Icons.chevronRight
            className={cn(
              'h-3 w-3 text-foreground-muted transition-transform',
              rawOpen && 'rotate-90'
            )}
          />
          <span className="text-xs font-semibold text-foreground uppercase tracking-wide">
            Raw JSON
          </span>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <pre className="pl-4 text-[10px] font-mono text-foreground-muted whitespace-pre-wrap break-all max-h-[400px] overflow-y-auto">
            {JSON.stringify(snapshot, null, 2)}
          </pre>
        </CollapsibleContent>
      </Collapsible>
    </YStack>
  );
}
